import Image from "next/image";
import { useState } from "react";

import type { GeneratedCase, PageTestSource } from "@/lib/qa-types";

interface GeneratorViewProps {
    cases: GeneratedCase[];
    isGenerating: boolean;
    source: PageTestSource | null;
    onGenerate: (requirement: string) => void;
    onExecute: () => void;
}

const categoryLabels = { positive: "正向", negative: "逆向", boundary: "边界" };

const defaultRequirement = "优惠券仅可在订单金额满 100 元时使用；过期优惠券必须拒绝，且不能清空用户购物车。";

export function GeneratorView({ cases, isGenerating, source, onGenerate, onExecute }: GeneratorViewProps) {
    const [requirement, setRequirement] = useState(source?.requirement ?? defaultRequirement);

    return (
        <section className="workspace-view">
            <div className="view-heading">
                <div>
                    <p className="eyebrow">SCENARIO GENERATOR</p>
                    <h1>从需求到可执行用例</h1>
                    <p>导入 PRD 或 OpenAPI，生成带来源追踪、假设和质量校验的结构化用例。</p>
                </div>
                <span className="model-badge">Mock LLM · prompt@1.0</span>
            </div>
            {source?.exploration && <PageAnalysisPanel isAnalyzing={isGenerating} source={source} />}
            <div className="generator-layout">
                <div className="card source-card">
                    <label htmlFor="source-name">需求来源</label>
                    <div className="file-input">
                        <span>{source ? "WEB" : "DOC"}</span>
                        <div>
                            <strong>{source?.title ?? "商城结算需求_V2.3.md"}</strong>
                            <small>{source ? `${source.target_url} · 页面分析已接入` : "PRD · 18.4 KB · 已解析"}</small>
                        </div>
                        <button>更换</button>
                    </div>
                    {source && (
                        <div className="scenario-characteristics">
                            <strong>页面特性</strong>
                            {source.characteristics.slice(0, 6).map((item) => (
                                <span key={item}>{item}</span>
                            ))}
                        </div>
                    )}
                    <label htmlFor="requirement">需求片段</label>
                    <textarea id="requirement" onChange={(event) => setRequirement(event.target.value)} value={requirement} />
                    <div className="source-options">
                        <span>✓ 正向</span>
                        <span>✓ 逆向</span>
                        <span>✓ 边界值</span>
                    </div>
                    <button
                        className="button primary wide"
                        disabled={isGenerating || requirement.trim().length < 10}
                        onClick={() => onGenerate(requirement)}
                    >
                        {isGenerating ? "正在生成并校验…" : "生成结构化用例"}
                    </button>
                </div>
                <div className="card cases-card">
                    <div className="case-summary">
                        <div>
                            <p className="section-kicker">生成结果</p>
                            <h2>{cases.length ? `${cases.length} 条候选用例` : "等待生成"}</h2>
                        </div>
                        {cases.length > 0 && (
                            <button className="button primary" onClick={onExecute}>
                                下发 {cases.length} 条场景到任务执行
                            </button>
                        )}
                        {cases.length > 0 && <span>Schema 校验通过</span>}
                    </div>
                    {cases.length === 0 ? (
                        <div className="empty-state">
                            <div>AI</div>
                            <p>配置左侧输入并开始生成</p>
                            <span>模型输出不会直接投入执行，必须先经过结构化校验与人工评审。</span>
                        </div>
                    ) : (
                        <div className="case-list">
                            {cases.map((item) => (
                                <article className="case-item" key={item.case_id}>
                                    <div>
                                        <span className={`case-category ${item.category}`}>{categoryLabels[item.category]}</span>
                                        <span className="priority">{item.priority}</span>
                                    </div>
                                    <h3>{item.title}</h3>
                                    <p>
                                        {item.case_id} · {item.steps.length} 个步骤 · {item.expected_results.length} 个断言
                                    </p>
                                    <button>查看与评审 →</button>
                                </article>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

function PageAnalysisPanel({ source, isAnalyzing }: { source: PageTestSource; isAnalyzing: boolean }) {
    const exploration = source.exploration!;
    const stages = [
        { label: "加载远程页面", detail: exploration.current_url },
        { label: "解析 DOM 与可访问树", detail: `发现 ${exploration.elements.length} 个可交互元素` },
        { label: "提取页面语义特性", detail: `${source.characteristics.length} 条页面特性已进入上下文` },
        { label: "构建测试场景", detail: isAnalyzing ? "正在生成正向、逆向与边界场景" : "结构化场景已通过 Schema 校验" },
    ];

    return (
        <section className={`card page-analysis-panel ${isAnalyzing ? "analyzing" : "complete"}`} aria-label="页面元素分析过程">
            <header className="page-analysis-head">
                <div>
                    <p className="section-kicker">AGENT PAGE ANALYSIS</p>
                    <h2>页面元素分析过程</h2>
                    <p>Agent 正在读取远程页面证据，并将可操作元素转换为测试上下文。</p>
                </div>
                <span className="analysis-live-status">
                    <i /> {isAnalyzing ? "正在分析" : "分析完成"}
                </span>
            </header>
            <div className="analysis-stage-track">
                {stages.map((stage, index) => {
                    const current = isAnalyzing && index === stages.length - 1;
                    return (
                        <div className={current ? "current" : "done"} key={stage.label}>
                            <b>{current ? "···" : "✓"}</b>
                            <span>
                                <strong>{stage.label}</strong>
                                <small>{stage.detail}</small>
                            </span>
                        </div>
                    );
                })}
            </div>
            <div className="page-analysis-grid">
                <div className="analysis-browser-frame">
                    <div className="analysis-browser-bar">
                        <span>●</span>
                        <span>●</span>
                        <span>●</span>
                        <strong>{exploration.current_url}</strong>
                        <small>快照 #{exploration.revision}</small>
                    </div>
                    <div className="analysis-screenshot">
                        <Image
                            alt={`${source.title} 的页面元素分析快照`}
                            height={720}
                            src={exploration.screenshot_data_url}
                            unoptimized
                            width={1280}
                        />
                        {isAnalyzing && <span className="analysis-scan-line" />}
                    </div>
                </div>
                <div className="analysis-element-inventory">
                    <div className="analysis-inventory-head">
                        <span>INTERACTIVE ELEMENTS</span>
                        <strong>{exploration.elements.length}</strong>
                    </div>
                    <div className="analysis-element-list">
                        {exploration.elements.slice(0, 10).map((element) => (
                            <div key={element.element_id}>
                                <b>{element.index}</b>
                                <span>
                                    <strong>{element.name || element.role}</strong>
                                    <small>
                                        {element.role} · {element.tag}
                                        {element.input_type ? ` · ${element.input_type}` : ""}
                                    </small>
                                </span>
                                <code>{element.disabled ? "DISABLED" : "READY"}</code>
                            </div>
                        ))}
                    </div>
                    <div className="analysis-agent-log" aria-live="polite">
                        <span>&gt; accessibility tree parsed</span>
                        <span>&gt; selectors normalized · sensitive fields masked</span>
                        <span>&gt; {isAnalyzing ? "generating scenario graph_" : "scenario graph ready"}</span>
                    </div>
                </div>
            </div>
        </section>
    );
}
